import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Switch, Divider, InputLabel, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import FormInput from "../../inputs/FormInput";
import LanguageToggle from "../../global/LanguageToggle";
import LocationsEditor from "./LocationsEditor";

import { useCreateShippingZone } from "../../../hooks/shipping/useCreateShippingZone";
import { useUpdateShippingZone } from "../../../hooks/shipping/useUpdateShippingZone";

const FormAddEditShippingZone = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [locations, setLocations] = useState([]);

    const { createShippingZoneMutate, isCreating } = useCreateShippingZone();
    const { updateShippingZoneMutate, isUpdating } = useUpdateShippingZone();
    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setIsActive(currentData?.is_active ?? true);
            setLocations(
                (currentData?.locations || []).map((loc) => ({
                    country_code: loc.country_code || loc.country,
                    state: loc.state || null,
                }))
            );
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            name: {
                en: nameEn || nameAr,
                ar: nameAr || nameEn,
            },
            is_active: isActive,
            locations,
        };

        const onDone = { onSuccess: () => onSubmitSuccess && onSubmitSuccess() };

        if (isEdit && currentData?.id) {
            updateShippingZoneMutate({ id: currentData.id, data: payload }, onDone);
        } else {
            createShippingZoneMutate(payload, onDone);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                    {isEdit
                        ? (isArabic ? "تعديل منطقة شحن" : "Edit Shipping Zone")
                        : (isArabic ? "إضافة منطقة شحن" : "Add Shipping Zone")}
                </Typography>
                <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
            </Box>

            <Divider />

            <Grid container spacing={3}>
                {activeLang === "ar" && (
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label="الاسم (عربي)"
                            placeholder="مثال: منطقة الخليج"
                            type="text"
                            value={nameAr}
                            onChange={(e) => setNameAr(e.target.value)}
                            required
                        />
                    </Grid>
                )}
                {activeLang === "en" && (
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label="Name (English)"
                            placeholder="e.g: Gulf Zone"
                            type="text"
                            value={nameEn}
                            onChange={(e) => setNameEn(e.target.value)}
                            required
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <InputLabel sx={{ fontWeight: "bold", mb: 1 }}>
                        {isArabic ? "تفعيل المنطقة" : "Active"}
                    </InputLabel>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        borderRadius: "12px",
                    }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            {isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "غير نشط" : "Inactive")}
                        </Typography>
                        <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <LocationsEditor locations={locations} onChange={setLocations} />
                </Grid>
            </Grid>

            <Divider />

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Box
                    component="button"
                    type="button"
                    onClick={() => onSubmitSuccess && onSubmitSuccess()}
                    disabled={loadingSubmit}
                    sx={{
                        cursor: "pointer",
                        bgcolor: "transparent",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "12px",
                        px: 4, py: 1,
                        fontWeight: "bold",
                        color: "text.primary",
                    }}
                >
                    {isArabic ? "إلغاء" : "Cancel"}
                </Box>
                <Box
                    component="button"
                    type="submit"
                    disabled={loadingSubmit}
                    sx={{
                        cursor: "pointer",
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        border: "none",
                        borderRadius: "12px",
                        px: 4, py: 1,
                        fontWeight: "bold",
                        minWidth: 160,
                        boxShadow: theme.shadows[2],
                    }}
                >
                    {loadingSubmit
                        ? (isArabic ? "جاري الحفظ..." : "Saving...")
                        : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "إنشاء" : "Create"))}
                </Box>
            </Box>
        </Box>
    );
};

export default FormAddEditShippingZone;
